import React, { useState } from 'react';
import { FiMenu, FiTrash2, FiStar, FiMic, FiMusic, FiChevronDown, FiChevronRight, FiUsers, FiEdit2 } from 'react-icons/fi';
import { actDurationSecs, formatSecs, parseDurationInput } from '../../../utils/recitalDuration';
import { resolveColor, lookStyles } from '../colorLook';

// Click-to-edit stage time. Shows "2:30"; accepts "2:30", "2.5" or "3" on edit.
function DurationBadge({ act, onDurationChange }) {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState('');
    const secs = actDurationSecs(act);

    const commit = () => {
        setEditing(false);
        const parsed = parseDurationInput(value);
        if (parsed != null && parsed !== secs && onDurationChange) {
            onDurationChange(act.id, parsed);
        }
    };

    if (editing) {
        return (
            <input
                autoFocus
                value={value}
                onChange={e => setValue(e.target.value)}
                onBlur={commit}
                onKeyDown={e => {
                    if (e.key === 'Enter') commit();
                    if (e.key === 'Escape') setEditing(false);
                }}
                placeholder="2:30"
                style={{ width: 52, fontSize: 12, padding: '2px 5px', border: '1px solid #29b8e5',
                    borderRadius: 5, color: '#3f3f55', textAlign: 'right' }}
            />
        );
    }

    return (
        <div
            title="Click to edit stage time"
            onClick={e => { e.stopPropagation(); setValue(formatSecs(secs)); setEditing(true); }}
            style={{ fontSize: 12, color: '#6b6b8a', whiteSpace: 'nowrap', cursor: 'pointer',
                borderBottom: '1px dashed #c0c0d8' }}>
            {secs != null ? formatSecs(secs) : '—:—'}
        </div>
    );
}

const TYPE_ICONS = {
    intermission: <FiStar size={13}/>,
    custom:       <FiMic size={13}/>,
    unlisted:     <FiMusic size={13}/>,
};

export default function ActCard({ act, index, actNumber, colorBy, colorLook = 'stripe', colorMap, colorToLevelName, quickChangeIds, onDelete,
    onDurationChange, onEditRoutine, onDragStart, onDragOver, onDrop, onDragEnd, isDragOver }) {

    const [expanded, setExpanded] = useState(false);
    const isSpecial = act.type !== 'class';
    // Prefer the recital's own class copy; legacy acts fall back to the sticker
    const ss = act.recitalClass || act.scheduleSticker;

    // The class colour under "Color by", shown the way the recital's look says.
    const bgColor = isSpecial ? null : resolveColor(ss, colorBy, colorMap);
    const look = lookStyles(bgColor, colorLook);

    const isQuickChange = quickChangeIds?.has(act.id);
    const students = ss?.students || [];
    // A routine is a recital-only class: added here, not copied from the schedule.
    const isRoutine = !!act.recitalClass && act.recitalClass.sourceStickerId == null;

    const cardStyle = {
        background: isSpecial ? '#f5f5fa' : '#fff',
        border: `1px solid ${isQuickChange ? '#e07b2a' : '#e0e0ec'}`,
        ...(isSpecial ? {} : look.card),
        borderRadius: 9,
        marginBottom: 6,
        boxShadow: isDragOver ? '0 0 0 2px #29b8e5' : 'none',
        transition: 'box-shadow 0.1s',
        userSelect: 'none',
        overflow: 'hidden',
    };

    const rowStyle = {
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 14px', cursor: 'default',
    };

    if (isSpecial) {
        return (
            <div style={cardStyle}>
                <div style={rowStyle}
                    draggable
                    onDragStart={() => onDragStart(act.id)}
                    onDragOver={e => { e.preventDefault(); onDragOver(act.id); }}
                    onDrop={() => onDrop(act.id)}
                    onDragEnd={onDragEnd}>
                    <div style={{ cursor:'grab', color:'#b0b0c8', display:'flex' }}><FiMenu size={14}/></div>
                    <div style={{ color:'#6b6b8a', display:'flex' }}>{TYPE_ICONS[act.type] || <FiStar size={13}/>}</div>
                    <div style={{ flex:1, fontSize:13, fontStyle:'italic', color:'#6b6b8a', fontWeight:500 }}>
                        {act.label || (act.type === 'intermission' ? 'Intermission' : 'Custom moment')}
                    </div>
                    <button onClick={() => onDelete(act.id)} style={{ background:'none', border:'none', cursor:'pointer', color:'#c0c0d0', display:'flex', padding:2 }}>
                        <FiTrash2 size={13}/>
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={cardStyle}>
            <div style={rowStyle}
                draggable
                onDragStart={() => onDragStart(act.id)}
                onDragOver={e => { e.preventDefault(); onDragOver(act.id); }}
                onDrop={() => onDrop(act.id)}
                onDragEnd={onDragEnd}>
                <div style={{ cursor:'grab', color:'#b0b0c8', display:'flex' }}><FiMenu size={14}/></div>
                <div style={{ width:26, height:26, borderRadius:6, background: look.marker || bgColor || '#e0e0ec',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize:11, fontWeight:700, color:'#fff', flexShrink:0 }}>
                    {actNumber}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:14, fontWeight:600, color:'#3f3f55', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                        {ss?.className || act.label || 'Untitled act'}
                        {isRoutine && (
                            <span title="Added in this recital, not on the schedule"
                                style={{ fontSize:9, fontWeight:600, color:'#8b35c8', background:'#f5edff', border:'1px solid #c8a0f0',
                                    borderRadius:3, padding:'1px 5px', marginLeft:6, verticalAlign:'middle', letterSpacing:0.3 }}>
                                ROUTINE
                            </span>
                        )}
                        {!ss && act.type === 'class' && !act.recitalClassId && (
                            <span title="This class was removed from the schedule. The act stays in your running order. Remove it here if it should go too."
                                style={{ fontSize:10, fontWeight:500, color:'#e07b2a', marginLeft:6 }}>
                                (no longer on the schedule)
                            </span>
                        )}
                    </div>
                    <div style={{ fontSize:11, color:'#6b6b8a', marginTop:2 }}>
                        {isRoutine ? `${students.length} dancer${students.length === 1 ? '' : 's'} · not on the schedule` : [
                            ...(ss?.styles||[]),
                            ...(ss?.teachers||[]),
                            ...(ss?.levels||[]).map(c => (colorToLevelName && colorToLevelName[c]) || null).filter(Boolean),
                        ].join(' · ')}
                    </div>
                </div>
                <DurationBadge act={act} onDurationChange={onDurationChange} />
                {isRoutine && onEditRoutine && (
                    <button title="Edit name and roster"
                        onClick={e => { e.stopPropagation(); onEditRoutine(act.recitalClass); }}
                        style={{ background:'none', border:'none', cursor:'pointer', color:'#6b6b8a', display:'flex', padding:2 }}>
                        <FiEdit2 size={12}/>
                    </button>
                )}
                {isQuickChange && (
                    <div title="Quick change warning" style={{ color:'#e07b2a' }}>
                        <span style={{ fontSize:13 }}>⚠</span>
                    </div>
                )}
                {students.length > 0 && (
                    <button
                        onClick={e => { e.stopPropagation(); setExpanded(o => !o); }}
                        style={{ background:'none', border:'none', cursor:'pointer', color:'#6b6b8a',
                            display:'flex', alignItems:'center', gap:3, padding:2, fontSize:11 }}>
                        <FiUsers size={12}/>
                        <span>{students.length}</span>
                        {expanded ? <FiChevronDown size={11}/> : <FiChevronRight size={11}/>}
                    </button>
                )}
                <button onClick={() => onDelete(act.id)} style={{ background:'none', border:'none', cursor:'pointer', color:'#c0c0d0', display:'flex', padding:2 }}>
                    <FiTrash2 size={13}/>
                </button>
            </div>

            {/* Student list */}
            {expanded && students.length > 0 && (
                <div style={{ borderTop:'1px solid #f0f0f8', padding:'8px 14px 10px',
                    background: colorLook === 'fill' && bgColor ? `color-mix(in srgb, ${bgColor} 8%, white)` : '#fafafe' }}>
                    <div style={{ fontSize:11, fontWeight:600, color:'#9b9bb8', marginBottom:6,
                        textTransform:'uppercase', letterSpacing:'0.04em' }}>
                        Students ({students.length})
                    </div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
                        {students.map(s => (
                            <span key={s.id} style={{ fontSize:12, color:'#3f3f55', background:'#fff',
                                border:'1px solid #e0e0ec', borderRadius:6, padding:'3px 8px' }}>
                                {s.name}
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
